import { Component } from '@theme/component';
import { DialogCloseEvent } from '@theme/dialog';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Hutjens: compact lightbox for the product media gallery.
 * Shows one image at a time; videos and 3D models are skipped (they keep working in the gallery itself).
 * Keeps the public API of the original ZoomDialog: open(index, event) and close().
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLImageElement} image - The single image shown in the lightbox.
 * @property {HTMLElement} stage - The area around the image (click closes).
 * @property {HTMLElement} counter - The "2 / 5" counter.
 * @property {HTMLButtonElement} prev - The previous button.
 * @property {HTMLButtonElement} next - The next button.
 * @property {HTMLScriptElement} data - JSON with one entry per gallery media index (null for non-images).
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'image', 'stage', 'counter', 'prev', 'next', 'data'];

  /** @type {Array<{src: string, srcset: string, alt: string} | null> | null} */
  #items = null;
  /** @type {number[]} media indexes that are images */
  #order = [];
  #position = 0;
  /** @type {HTMLElement | null} */
  #trigger = null;
  /** @type {{x: number, y: number} | null} */
  #touchStart = null;
  #justSwiped = false;

  connectedCallback() {
    super.connectedCallback();
    const { dialog, stage } = this.refs;
    dialog.addEventListener('close', this.#handleClose);
    stage.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    stage.addEventListener('touchend', this.#handleTouchEnd, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { dialog, stage } = this.refs;
    dialog?.removeEventListener('close', this.#handleClose);
    stage?.removeEventListener('touchstart', this.#handleTouchStart);
    stage?.removeEventListener('touchend', this.#handleTouchEnd);
    document.documentElement.removeAttribute('scroll-lock');
  }

  #getItems() {
    if (this.#items) return this.#items;

    try {
      this.#items = JSON.parse(this.refs.data.textContent || '[]');
    } catch {
      this.#items = [];
    }

    const items = this.#items || [];
    this.#order = items.map((item, index) => (item ? index : -1)).filter((index) => index >= 0);
    return items;
  }

  /**
   * Opens the lightbox on the clicked media.
   *
   * @param {number | string} index - The gallery media index.
   * @param {Event} [event] - The triggering event.
   */
  open(index, event) {
    const items = this.#getItems();
    const mediaIndex = Number(index);

    // Video / 3D model: leave the gallery behaviour alone.
    if (!items[mediaIndex]) return;

    event?.preventDefault();

    const target = event?.target instanceof Element ? event.target : document.activeElement;
    this.#trigger =
      target instanceof Element
        ? /** @type {HTMLElement | null} */ (target.closest('button, a[href], slideshow-slide, li'))
        : null;

    this.#position = Math.max(0, this.#order.indexOf(mediaIndex));
    this.#render();

    const { dialog } = this.refs;
    if (!dialog.open) dialog.showModal();
    document.documentElement.setAttribute('scroll-lock', '');
  }

  close() {
    const { dialog } = this.refs;
    if (dialog.open) dialog.close();
    // The native 'close' event is not reliably delivered in this theme, so clean up directly.
    this.#handleClose();
  }

  /** Kept for compatibility with code that called the original method. */
  closeDialog() {
    this.close();
  }

  previous() {
    this.#go(-1);
  }

  next() {
    this.#go(1);
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.previous();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.next();
        break;
    }
  }

  /**
   * Closes when clicking the dark area around the image.
   * @param {MouseEvent} event
   */
  handleBackdropClick(event) {
    if (this.#justSwiped) return;

    // The theme's event delegation rewrites event.target to the [on:click] element, so use the real origin.
    const origin = event.composedPath?.()[0] ?? event.target;
    const { dialog, stage } = this.refs;
    if (origin === dialog || origin === stage) this.close();
  }

  /**
   * @param {number} step
   */
  #go(step) {
    const count = this.#order.length;
    if (count < 2) return;

    this.#position = (this.#position + step + count) % count;
    this.#render();
  }

  #render() {
    const items = this.#getItems();
    const { image, counter, prev, next } = this.refs;
    const count = this.#order.length;
    const item = items[this.#order[this.#position]];
    if (!item) return;

    if (image.getAttribute('src') !== item.src) {
      image.onload = () => {
        image.onload = null;
        if (!prefersReducedMotion() && this.refs.dialog.open) {
          image.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 150, easing: 'ease-out' });
        }
      };
      image.sizes = '90vw';
      image.srcset = item.srcset;
      image.src = item.src;
    }
    image.alt = item.alt || '';

    counter.textContent = `${this.#position + 1} / ${count}`;
    counter.hidden = count < 2;
    prev.hidden = count < 2;
    next.hidden = count < 2;

    // Preload the neighbouring images so navigating feels instant.
    for (const offset of [-1, 1]) {
      if (count < 2) break;
      const neighbour = items[this.#order[(this.#position + offset + count) % count]];
      if (!neighbour) continue;
      const preload = new Image();
      preload.sizes = '90vw';
      preload.srcset = neighbour.srcset;
      preload.src = neighbour.src;
    }
  }

  #handleClose = () => {
    if (!document.documentElement.hasAttribute('scroll-lock') && !this.#trigger) return;

    document.documentElement.removeAttribute('scroll-lock');
    window.dispatchEvent(new DialogCloseEvent());

    const trigger = this.#trigger;
    this.#trigger = null;
    if (!trigger || !trigger.isConnected) return;

    if (!trigger.matches('button, a[href], [tabindex]')) trigger.setAttribute('tabindex', '-1');
    trigger.focus({ preventScroll: true });
  };

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    if (event.touches.length !== 1) {
      this.#touchStart = null;
      return;
    }
    const touch = event.touches[0];
    if (!touch) return;
    this.#touchStart = { x: touch.clientX, y: touch.clientY };
  };

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    const start = this.#touchStart;
    this.#touchStart = null;
    const touch = event.changedTouches[0];
    if (!start || !touch || event.touches.length > 0) return;

    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    if (Math.abs(deltaX) < 50 || Math.abs(deltaX) < Math.abs(deltaY) * 1.5) return;

    this.#justSwiped = true;
    setTimeout(() => (this.#justSwiped = false), 350);

    if (deltaX < 0) this.next();
    else this.previous();
  };
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
